import React, { useMemo } from "react";
import {
  EuiModal,
  EuiModalHeader,
  EuiModalHeaderTitle,
  EuiModalBody,
  EuiModalFooter,
  EuiText,
  EuiAccordion,
  EuiSpacer,
  EuiButton,
  EuiOverlayMask,
} from "@elastic/eui";

const WORKSPACE_COPY = {
  practice: {
    title: "Practice Prompt Guide",
    intro:
      "Use this reference to design high-yield drill prompts that match the topics and difficulty you need. Lean on it whenever you want fresh inspiration or to recalibrate your practice set.",
    tips: [
      "Specify the topic, subtopic, and desired difficulty to narrow the scope.",
      "Ask for a mix of question formats (multiple choice, free response, conceptual, numerical).",
      "Request answer keys or step-by-step solutions so you can self-check.",
      "Include reflections at the end: What patterns or misconceptions should you watch for?",
    ],
    note: "You can reopen the full guide anytime from the Prompt Guide button near the message box.",
  },
  study: {
    title: "Study Coach Prompt Guide",
    intro:
      "This guide walks you through planning focused study sessions. Use it to structure goals, checkpoints, and reflection prompts that keep you accountable.",
    tips: [
      "Kick off with your exam date, available study blocks, and the units you must cover.",
      "Ask for spaced review suggestions and minimum viable outcomes for each session.",
      "Request self-check questions or challenges at the end of each block to measure progress.",
      "Add a reflection checkpoint so you can adjust the plan after every session.",
    ],
    note: "Keep the guide handy—revisit it whenever you start a new study cycle.",
  },
  default: {
    title: "Prompt Guide",
    intro:
      "Use this guide to help you craft clear, targeted prompts that deliver the support you need.",
    tips: [
      "Describe your goal, context, and any constraints (time, resources, prior knowledge).",
      "Clarify the format and depth you expect in the response.",
      "Request follow-up checks so you can verify accuracy or extend the plan.",
    ],
    note: "The guide is always available from the Prompt Guide button beneath the chat input.",
  },
};

const PromptGuideModal = ({
  isOpen,
  workspaceMode,
  onClose,
  onOpenGuide,
  guideUrl,
}) => {
  const guideAvailable = Boolean(guideUrl);

  if (!isOpen) {
    return null;
  }

  const copy = WORKSPACE_COPY[workspaceMode] || WORKSPACE_COPY.default;

  return (
    <EuiOverlayMask>
      <EuiModal onClose={onClose} initialFocus="[data-focus]">
        <EuiModalHeader>
          <EuiModalHeaderTitle>{copy.title}</EuiModalHeaderTitle>
        </EuiModalHeader>
        <EuiModalBody>
          <EuiText size="m">
            <p>{copy.intro}</p>
          </EuiText>
          {copy.note && (
            <>
              <EuiSpacer size="m" />
              <EuiText size="s" color="subdued">
                <p>{copy.note}</p>
              </EuiText>
            </>
          )}
          {!guideAvailable && (
            <>
              <EuiSpacer size="m" />
              <EuiText size="s" color="subdued">
                <p>The prompt guide link will be added soon. Stay tuned!</p>
              </EuiText>
            </>
          )}
        </EuiModalBody>
        <EuiModalFooter>
          <EuiButton fill iconType="popout" onClick={onOpenGuide}>
            {guideAvailable ? "Open Prompt Guide" : "Guide coming soon"}
          </EuiButton>
        </EuiModalFooter>
      </EuiModal>
    </EuiOverlayMask>
  );
};

export default PromptGuideModal;
